import { Row } from "@tanstack/react-table";
import {
  FileText,
  Folder,
  LayoutGrid,
  PencilLine,
  SlidersIcon,
  Trash2,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useProductTableColumns } from "./ProductTableColumns";

import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryID from "@/hooks/useQueryId";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { Permissions } from "@/interfaces/globals";
import {
  IProduct,
  IProductLogsItem,
  ProductType,
} from "@/interfaces/tools.interface";
import { hasWritePermission } from "@/redux/slices/permissionSlice";

interface ProductTableProps {
  data: IProduct[];
  pagination: ResponsePagination;
  toggleAddProductModal: () => void;
  toggleEditProductModal: () => void;
  toggleVieLogSheet: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      itemName: string;
      data: IProductLogsItem[];
    } | null>
  >;
  toggleProductDetailSheet: () => void;
  toggleAdjustQuantityModal: () => void;
  toggleViewOnSiteSheet: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
    } | null>
  >;
}

const ProductTable = ({
  data,
  toggleAddProductModal,
  toggleEditProductModal,
  pagination,
  toggleVieLogSheet,
  toggleProductDetailSheet,
  toggleAdjustQuantityModal,
  toggleViewOnSiteSheet,
}: ProductTableProps): JSX.Element => {
  const { set } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);
  const navigate = useNavigate();

  const getActionOptions = (row: Row<IProduct>): ActionButtonDataTypes[] => {
    const defaultActions: ActionButtonDataTypes[] = [
      {
        icon: Folder,
        name: "Product Details",
        onclick: (): void => {
          set(row.original.id);
          toggleProductDetailSheet();
        },
      },
      {
        icon: FileText,
        name: "View Log",
        onclick: (): void => {
          toggleVieLogSheet({
            isOpen: true,
            itemName: row.original.name,
            data: row.original.logs,
          });
        },
      },
      {
        icon: PencilLine,
        name: "Edit",
        onclick: (): void => {
          set(row.original.id);
          toggleEditProductModal();
        },
      },
      {
        icon: Trash2,
        name: "Move to Trash",
        isTrash: true,
        onclick: (): void => {
          toast.info("Edit Ticket");
        },
      },
    ];

    const subProductsOnlyActions: ActionButtonDataTypes[] = [
      {
        icon: LayoutGrid,
        name: "View Sub Products",
        onclick: (): void => {
          navigate(`${ROUTES.TOOLS}${ROUTES.PRODUCTS}/${row.original.id}`);
        },
      },
    ];

    if (row.original.type === ProductType.Has_Sub_Product) {
      return [...subProductsOnlyActions, ...defaultActions];
    }

    if (row.original?.type === ProductType.Has_Quantity) {
      defaultActions.splice(2, 0, {
        icon: SlidersIcon,
        name: "Adjust Quantity",
        onclick: (): void => {
          set(row.original.id);
          toggleAdjustQuantityModal();
        },
      });
    }

    return defaultActions;
  };

  const writePermissions = useSelector(hasWritePermission(Permissions.TOOLS));

  const columns = useProductTableColumns({
    getActions: getActionOptions,
    writePermissions,
    toggleViewOnSiteSheet,
  });

  const handleRowClick = (id: string): void => {
    const selectedProduct = data.find((product) => product.id === id);
    if (selectedProduct?.type === ProductType.Has_Sub_Product) {
      navigate(`${ROUTES.TOOLS}${ROUTES.PRODUCTS}/${id}`);
    } else {
      set(id);
      toggleProductDetailSheet();
    }
  };

  return (
    <div>
      <CustomTable
        empty={{}}
        columns={columns}
        data={data}
        title={ToolsConstants.INDEX_CONTENT_TEXT.PRODUCT_HEADER}
        hasWritePermission={writePermissions}
        functions={{
          isExport: true,
          columnToggle: true,
          search: ToolsConstants.INDEX_CONTENT_TEXT.PRODUCT_HEADER,
          RightClickData: (
            row: GlobalRowDataTypes<IProduct>,
          ): ActionButtonDataTypes[] => getActionOptions(row as Row<IProduct>),
          add: {
            label: ToolsConstants.INDEX_CONTENT_TEXT.ADD_NEW_PRODUCTS,
            handleClick: toggleAddProductModal,
          },
          onRowClick: handleRowClick,
          pagination,
        }}
        hide={{
          filters: true,
          functions: true,
        }}
      />
    </div>
  );
};

export default ProductTable;
